import { Extension } from '@tiptap/core';
import { PluginKey, Plugin, TextSelection } from '@tiptap/pm/state';
import { getSpecialNodeTypes, changedDescendants, getToPath, getPreviousNode, getPath, getFromPath, getNextNode, getPreviousSibling, getLastDescendant } from './utils.js';
import { createLogger } from '@fabric-app/utils/logging';
import { Fragment } from '@tiptap/pm/model';
import { clamp } from '@fabric-app/utils/number';

const logger = createLogger("Tiptap-FbcLogic");
const processNodeChildrenForIndent = (node, start, to) => {
  const keptChildren = [];
  const liftedChildren = [];
  node.forEach((child, childOffset, childIndex) => {
    if (node.inlineContent) {
      keptChildren.push(child);
      return;
    }
    const pos = start + childOffset;
    if (pos <= to || childIndex === 0) {
      const { kept: childKept, lifted: childLifted } = processNodeChildrenForIndent(child, pos, to);
      keptChildren.push(child.copy(Fragment.from(childKept)), ...childLifted);
    } else {
      liftedChildren.push(child);
    }
  });
  return {
    kept: keptChildren,
    lifted: liftedChildren
  };
};
const FbcLogicPluginKey = new PluginKey("fbcLogic");
const FbcLogic = Extension.create({
  name: "fbcLogic",
  addCommands() {
    return {
      doBackspace: () => ({ tr, state, chain }) => {
        const { from, to } = state.selection;
        if (from !== to) {
          logger.debug({
            from,
            to
          });
          return false;
        }
        const { paragraphBlock, toggleList } = getSpecialNodeTypes(state.schema);
        const path = getFromPath(state.selection);
        if (path.length <= 1) {
          return false;
        }
        const parent = path[0];
        const grandParent = path[1];
        if (parent.pos <= 1 || from > parent.start) {
          return false;
        }
        const isInsideTableCell = grandParent.node.type.name === "tableCell" || grandParent.node.type.name === "tableHeader";
        if (parent.index === 0 && isInsideTableCell) {
          return false;
        }
        const inlineContent = parent.node.type.inlineContent;
        const isParentFirstChild = parent.index === 0;
        const isGrandParentList = grandParent.node.type.isInGroup("list");
        const isGrandParentParagraphBlock = grandParent.node.type === paragraphBlock;
        const collapseGrandParent = inlineContent && isParentFirstChild && (isGrandParentList || grandParent.node.childCount === 1 && parent.node.content.size === 0);
        if (isGrandParentParagraphBlock) {
          tr.replaceWith(grandParent.pos, grandParent.pos + grandParent.node.nodeSize - 1, grandParent.node.content);
          tr.setSelection(TextSelection.create(tr.doc, grandParent.start));
          return chain().doBackspace().run();
        }
        if (grandParent.node.childCount === 1 && collapseGrandParent) {
          tr.replaceWith(grandParent.pos, grandParent.pos + grandParent.node.nodeSize - 1, grandParent.node.content);
          tr.setSelection(TextSelection.create(tr.doc, tr.mapping.map(grandParent.pos) + 1));
          return true;
        } else if (collapseGrandParent) {
          const newParagraphBlock = paragraphBlock.createAndFill(null, grandParent.node.content);
          if (!newParagraphBlock) {
            logger.warn({
              grandParentContent: grandParent.node.content
            }, "Failed to create new paragraphBlock for lifting content. Aborting this specific lifting path.");
          } else {
            tr.replaceWith(grandParent.pos, grandParent.pos + grandParent.node.nodeSize - 1, newParagraphBlock);
            tr.setSelection(TextSelection.create(tr.doc, tr.mapping.map(grandParent.pos) + 1));
            return true;
          }
        }
        if (!isInsideTableCell && parent.index === grandParent.node.childCount - 1 && !isParentFirstChild && grandParent.node.type.name !== "doc" && (grandParent.node.type !== toggleList || grandParent.node.childCount > 2)) {
          tr.delete(parent.pos, parent.end + 1);
          const newPos = tr.mapping.map(grandParent.end + 1);
          tr.insert(newPos, parent.node.copy(parent.node.content));
          tr.setSelection(TextSelection.create(tr.doc, newPos + 1));
          return true;
        }
        if ((parent.index > 0 || path.length === 2) && inlineContent) {
          const previousSibling = getPreviousSibling(path);
          if (!previousSibling || previousSibling.node.type.isAtom) {
            return false;
          } else {
            const lastDescendant = getLastDescendant(previousSibling) ?? previousSibling;
            tr.delete(parent.pos, parent.end);
            if (lastDescendant.node.type.inlineContent) {
              tr.insert(lastDescendant.end, parent.node.content);
              tr.setSelection(TextSelection.create(tr.doc, lastDescendant.end));
            } else {
              tr.insert(lastDescendant.end, parent.node.copy(parent.node.content));
              tr.setSelection(TextSelection.create(tr.doc, lastDescendant.end));
            }
            return true;
          }
        }
        return false;
      },
      doIndent: () => ({ tr, state }) => {
        const { from, to } = state.selection;
        const { paragraph, paragraphBlock } = getSpecialNodeTypes(state.schema);
        let path = void 0;
        let newFrom = from;
        let newTo = to;
        while (path !== null) {
          if (path === void 0) {
            path = getFromPath(state.selection);
          } else if (path) {
            path = getNextNode(path);
            if (path) path = getPath(tr.doc.resolve(tr.mapping.map(path[0].start)));
          }
          if (!path || path[0].pos >= to) break;
          const parent = path[0];
          let targetToIndent = parent;
          let targetToIndentDepth = 0;
          for (let i = 0; i < path.length - 1; i++) {
            const pathNode = path[i];
            targetToIndent = pathNode;
            targetToIndentDepth = i;
            if (pathNode.end >= to && !(pathNode.node.inlineContent && pathNode.index === 0)) {
              break;
            }
          }
          let previousSibling = getPreviousSibling(path, targetToIndentDepth);
          let createdNewParagraphBlock = false;
          if (previousSibling && previousSibling.node.type === paragraph && (previousSibling.index !== 0 || path.length - targetToIndentDepth <= 2)) {
            const newParagraphBlock = paragraphBlock.createAndFill(null, previousSibling.node);
            if (!newParagraphBlock) {
              logger.warn("Failed to create new paragraphBlock for lifting content. Aborting this specific lifting path.");
              continue;
            } else {
              tr.replaceWith(previousSibling.pos, previousSibling.end + 1, newParagraphBlock);
              previousSibling = getPath(tr.doc.resolve(previousSibling.start))[0];
              createdNewParagraphBlock = true;
            }
          }
          if (!previousSibling || !previousSibling.node.canReplace(previousSibling.node.childCount, previousSibling.node.childCount, Fragment.from(targetToIndent.node))) {
            continue;
          }
          const { kept: keptChildren, lifted: liftedChildren } = processNodeChildrenForIndent(targetToIndent.node, targetToIndent.start, newTo);
          const pos = clamp(createdNewParagraphBlock ? tr.mapping.map(targetToIndent.pos) : targetToIndent.pos, 0, tr.doc.content.size);
          const end = clamp(createdNewParagraphBlock ? tr.mapping.map(targetToIndent.end) : targetToIndent.end, 0, tr.doc.content.size);
          tr.delete(pos, end).insert(previousSibling.end, Fragment.from(liftedChildren)).insert(previousSibling.end, targetToIndent.node.copy(Fragment.from(keptChildren)));
          if (targetToIndent.pos <= from && targetToIndent.end >= from) {
            const offset = from - targetToIndent.pos;
            newFrom = previousSibling.end + offset;
            const offsetTo = to - targetToIndent.pos;
            newTo = previousSibling.end + offsetTo;
          }
          if (targetToIndent.end + 1 >= to) {
            break;
          }
        }
        tr.setSelection(TextSelection.create(tr.doc, newFrom, newTo));
        return true;
      },
      doOutdent: () => ({ tr, state }) => {
        const { from, to } = state.selection;
        let path = void 0;
        let newTargetPosition = void 0;
        let newFrom = from;
        let newTo = to;
        while (path !== null) {
          if (path === void 0) {
            path = getToPath(state.selection);
          } else if (path) {
            path = getPreviousNode(path);
            if (!path || path[0].end + 1 < from) break;
            if (path) path = getPath(tr.doc.resolve(tr.mapping.map(path[0].start)));
          }
          if (!path) break;
          const parent = path[0];
          let targetToOutdent = parent;
          let targetToOutdentDepth = 0;
          for (let i = 0; i < path.length - 1; i++) {
            const pathNode = path[i];
            targetToOutdent = pathNode;
            targetToOutdentDepth = i;
            if (!pathNode.node.inlineContent || pathNode.index > 0) {
              break;
            }
          }
          const targetToOutdentParent = path[targetToOutdentDepth + 1];
          if (!targetToOutdentParent || targetToOutdentParent.node.type.name === "doc") {
            break;
          }
          const sunkNodes = [];
          targetToOutdentParent.node.forEach((child, _, childIndex) => {
            if (childIndex <= targetToOutdent.index) {
              return;
            }
            sunkNodes.push(child);
          });
          const newNode = targetToOutdent.node.copy(targetToOutdent.node.content.append(Fragment.from(sunkNodes)));
          tr.delete(targetToOutdent.pos, targetToOutdentParent.end + 1);
          if (!newTargetPosition) newTargetPosition = tr.mapping.map(targetToOutdentParent.end + 1);
          else newTargetPosition = tr.mapping.map(newTargetPosition - 1, 1);
          tr.insert(newTargetPosition, newNode);
          if (targetToOutdent.pos <= from && targetToOutdent.end >= from) {
            const offset = from - targetToOutdent.pos;
            const offsetTo = to - targetToOutdent.pos;
            newFrom = newTargetPosition + offset;
            newTo = newTargetPosition + offsetTo;
          }
        }
        tr.setSelection(TextSelection.create(tr.doc, newFrom, newTo));
        return true;
      }
    };
  },
  addKeyboardShortcuts() {
    return {
      Backspace: ({ editor }) => {
        return editor.chain().focus().doBackspace().run();
      },
      Tab: ({ editor }) => {
        return editor.chain().focus().doIndent().run();
      },
      "Shift-Tab": ({ editor }) => {
        return editor.chain().focus().doOutdent().run();
      }
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: FbcLogicPluginKey,
        appendTransaction: (_, oldState, newState) => {
          const { paragraphBlock } = getSpecialNodeTypes(newState.schema);
          const tr = newState.tr;
          const check = (node, pos) => {
            if (node.type !== paragraphBlock || node.childCount > 1) return;
            tr.replaceWith(pos, pos + node.nodeSize, node.content);
          };
          changedDescendants(oldState.doc, newState.doc, 0, check);
          return tr.docChanged ? tr : null;
        }
      })
    ];
  }
});

export { FbcLogic };
//# sourceMappingURL=FbcLogic.js.map
